<template>
  <div class="monarch-connect-wrapper">
    <button
      v-if="isConnected"
      class="btn-connected"
      disabled
    >
      Monarch Connected
    </button>
    <button
      v-else
      @click="openModal"
      class="btn-secondary"
      :disabled="checkingStatus"
    >
      {{ checkingStatus ? 'Checking...' : 'Connect Monarch' }}
    </button>

    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Connect to Monarch Money</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        
        <div v-if="error" class="error-alert">
          {{ error }}
        </div>

        <form @submit.prevent="handleSubmit">
          <div v-if="step === 'credentials'" class="form-step">
            <p class="helper-text">Enter your Monarch Money credentials to sync categories and transactions.</p>
            
            <div class="form-group">
              <label for="email">Email Address</label>
              <input 
                id="email"
                v-model="form.email" 
                type="email" 
                required 
                placeholder="you@example.com"
                :disabled="loading"
              />
            </div>
            
            <div class="form-group">
              <label for="password">Password</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                required
                placeholder="••••••••"
                :disabled="loading"
              />
            </div>

            <div class="form-group">
              <label for="totp">MFA Code <span class="optional-label">(optional)</span></label>
              <input
                id="totp"
                v-model="form.code"
                type="text"
                placeholder="123456"
                autocomplete="one-time-code"
                :disabled="loading"
              />
              <p class="field-hint">If your account has MFA enabled, enter the code now to skip the extra step.</p>
            </div>
          </div>

          <div v-if="step === 'mfa'" class="form-step">
            <div class="mfa-info">
              <p><strong>Multi-Factor Authentication Required</strong></p>
              <p>Please enter the code from your authenticator app.</p>
            </div>
            
            <div class="form-group">
              <label for="mfa">MFA Code</label>
              <input 
                id="mfa"
                v-model="form.code" 
                type="text" 
                required 
                placeholder="123456"
                autocomplete="one-time-code"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-text" @click="closeModal" :disabled="loading">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="loading">
              <span v-if="loading" class="spinner"></span>
              {{ loading ? 'Connecting...' : (step === 'mfa' ? 'Verify Code' : 'Login') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { syncAPI } from '../services/api'

const emit = defineEmits(['connected'])

const isOpen = ref(false)
const loading = ref(false)
const error = ref(null)
const step = ref('credentials') // 'credentials' | 'mfa'
const isConnected = ref(false)
const checkingStatus = ref(false)

const form = reactive({
  email: '',
  password: '',
  code: ''
})

// Check connection status on mount
onMounted(async () => {
  await checkConnectionStatus()
})

const checkConnectionStatus = async () => {
  checkingStatus.value = true
  try {
    const response = await syncAPI.getMonarchStatus()
    isConnected.value = response.data.connected
  } catch (err) {
    console.error('Error checking Monarch status:', err)
    isConnected.value = false
  } finally {
    checkingStatus.value = false
  }
}

const openModal = () => {
  isOpen.value = true
  resetForm()
}

const closeModal = () => {
  isOpen.value = false
  resetForm()
}

const resetForm = () => {
  step.value = 'credentials'
  form.email = ''
  form.password = ''
  form.code = ''
  error.value = null
  loading.value = false
}

const handleSubmit = async () => {
  error.value = null
  loading.value = true

  try {
    if (step.value === 'credentials') {
      await handleLogin()
    } else {
      await handleMfa()
    }
  } catch (err) {
    console.error('Monarch connection error:', err)
    error.value = err.response?.data?.error || 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

const handleLogin = async () => {
  const payload = {
    email: form.email,
    password: form.password
  }
  if (form.code) {
    payload.totp = form.code
  }
  const response = await syncAPI.monarchLogin(payload)

  if (response.data.mfa_required) {
    step.value = 'mfa'
    error.value = null
  } else if (response.data.success) {
    isConnected.value = true
    emit('connected')
    closeModal()
  } else {
    error.value = response.data.error || 'Login failed'
  }
}

const handleMfa = async () => {
  // Use login endpoint with TOTP code, leveraging the updated backend logic
  const response = await syncAPI.monarchLogin({
    email: form.email,
    password: form.password,
    totp: form.code
  })

  if (response.data.success) {
    isConnected.value = true
    emit('connected')
    closeModal()
  } else {
    error.value = response.data.error || 'MFA verification failed'
  }
}
</script>

<style scoped>
.monarch-connect-wrapper {
  display: inline-block;
  margin-left: 1rem;
}
.btn-secondary {
  background-color: #95a5a6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.btn-secondary:hover { background-color: #7f8c8d; }
.btn-secondary:disabled { opacity: 0.7; cursor: not-allowed; }
.btn-connected {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: default;
}
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex; justify-content: center; align-items: center; z-index: 1000;
}
.modal-content {
  background: white; border-radius: 8px; width: 90%; max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); overflow: hidden;
}
.modal-header {
  padding: 1.5rem; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center;
}
.modal-header h3 { margin: 0; color: #2c3e50; font-size: 1.25rem; }
.close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #7f8c8d; }
.form-step { padding: 1.5rem; }
.helper-text { color: #7f8c8d; margin-bottom: 1.5rem; font-size: 0.95rem; line-height: 1.5; }
.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; margin-bottom: 0.5rem; color: #2c3e50; font-weight: 500; font-size: 0.9rem; }
.form-group input {
  width: 100%; padding: 0.75rem; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem;
}
.form-group input:focus { border-color: #3498db; outline: none; }
.error-alert {
  background-color: #fee2e2; color: #c0392b; padding: 1rem; margin: 1rem 1.5rem 0; border-radius: 4px; font-size: 0.9rem;
}
.modal-footer {
  padding: 1rem 1.5rem; background-color: #f8f9fa; border-top: 1px solid #eee;
  display: flex; justify-content: flex-end; gap: 1rem;
}
.btn-primary {
  background-color: #3498db; color: white; border: none; padding: 0.75rem 1.5rem;
  border-radius: 4px; font-size: 1rem; cursor: pointer; transition: background-color 0.2s;
}
.btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
.btn-text {
  background: none; border: none; color: #7f8c8d; cursor: pointer; font-size: 1rem; padding: 0.75rem;
}
.btn-text:hover { color: #2c3e50; }
.mfa-info {
  background-color: #e8f6f3; padding: 1rem; border-radius: 4px; margin-bottom: 1.5rem; color: #0e6655;
}
.optional-label { font-weight: 400; color: #95a5a6; font-size: 0.85rem; }
.field-hint { color: #95a5a6; font-size: 0.8rem; margin-top: 0.35rem; line-height: 1.4; }
</style>